import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
export function WireframeSphere() {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!containerRef.current) return;
    // --- Setup Scene ---
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000);
    camera.position.z = 6;
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    containerRef.current.appendChild(renderer.domElement);
    // --- Create Objects ---
    const group = new THREE.Group();
    scene.add(group);
    // Outer Sphere
    const geometry = new THREE.SphereGeometry(1, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00d4ff,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.scale.set(2, 2, 2);
    group.add(sphere);
    // Inner Sphere
    const innerGeometry = new THREE.SphereGeometry(0.8, 16, 16);
    const innerMaterial = new THREE.MeshBasicMaterial({
      color: 0xff00ff,
      wireframe: true,
      transparent: true,
      opacity: 0.2
    });
    const innerSphere = new THREE.Mesh(innerGeometry, innerMaterial);
    innerSphere.scale.set(2, 2, 2);
    group.add(innerSphere);
    // Particles
    const particlesGeometry = new THREE.BufferGeometry();
    const particleCount = 100;
    const posArray = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount * 3; i += 3) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const r = 2.5 + Math.random() * 0.5;
      posArray[i] = r * Math.sin(phi) * Math.cos(theta);
      posArray[i + 1] = r * Math.sin(phi) * Math.sin(theta);
      posArray[i + 2] = r * Math.cos(phi);
    }
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.05,
      color: 0x00d4ff,
      transparent: true,
      opacity: 0.6
    });
    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);
    // --- Animation Loop ---
    const clock = new THREE.Clock();
    let animationId: number;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      const elapsedTime = clock.getElapsedTime();
      // Rotate Group
      group.rotation.y += 0.005;
      // Wobble Sphere
      sphere.rotation.x = Math.sin(elapsedTime * 0.5) * 0.1;
      innerSphere.rotation.x = Math.sin(elapsedTime * 0.5) * 0.1;
      renderer.render(scene, camera);
    };
    animate();
    // --- Handle Resize ---
    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);
    // --- Cleanup ---
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      // Dispose resources
      geometry.dispose();
      material.dispose();
      innerGeometry.dispose();
      innerMaterial.dispose();
      particlesGeometry.dispose();
      particlesMaterial.dispose();
      renderer.dispose();
    };
  }, []);
  return <div className="w-full h-[400px] relative" data-id="element-382">
      <div ref={containerRef} className="w-full h-full absolute inset-0" data-id="element-383" />

      {/* Overlay text */}
      <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none z-10" data-id="element-384">
        <span className="text-[10px] text-magenta font-cyber tracking-[0.2em] uppercase animate-pulse" data-id="element-385">
          Shield Integrity: 100%
        </span>
      </div>
    </div>;
}